import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import { ReportLevel, AdminReportsResponse } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import {
	HolisticAndTraitScoresRow,
	useLazyHolisticAndTraitScoresQuery,
} from "./adminPerformanceReportApiSlice";
import { HolisticAndTraitScoresTable } from "./HolisticAndTraitScoresTable";
import { HolisticAndTraitScoresChart } from "./HolisticAndTraitScoresChart";

export default function HolisticAndTraitScores() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<HolisticAndTraitScoresRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [callHolisticAndTraitScores] = useLazyHolisticAndTraitScoresQuery();

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callHolisticAndTraitScores(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
		});
	}, [districtFilter, schoolFilter]);

	return (
		<section>
			<h1>
				{reportLevel} Holistic and Trait Scores by {reportSubLevelName(reportLevel)}
			</h1>
			<p className="mt-4 print:hidden">
				This report shows total course scores and trait averages based on the most recent
				draft of each essay. To print or download this data, click the three dots icon.
			</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end">
							<DistrictSchoolFilters
								districtOptions={districtOptions}
								schoolOptions={schoolOptions}
								districtFilter={districtFilter}
								schoolFilter={schoolFilter}
								setDistrictFilter={setDistrictFilter}
								setSchoolFilter={setSchoolFilter}
							/>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={data.rows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="holistic-trait-scores"
								downloadExcelLink={`${aspUrl}/AdminReports/HolisticAndTraitScoresExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/HolisticAndTraitScoresCsv?${queryString}`}
							/>
						</div>

						<HolisticAndTraitScoresTable
							className="mt-4"
							level={reportLevel}
							data={data}
							hideRows={hideRows}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
						/>
						<div className="print:hidden">
							<HolisticAndTraitScoresChart
								level={reportLevel}
								data={data.rows}
								hasData={data.rows.length > 0}
							/>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
